import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import { LuX } from 'react-icons/lu'
import { MENU_HEIGHT, Z_INDEX } from '../constants'
import useBilling, { isFreePlan, isPaidPlan, type BillingAlert, type PlanRow } from '../models/useBilling'
import useOrganizations from '../models/useOrganizations'
import { capitalize, formatBytes, formatCents } from '../utilities/general'
import { PROVISIONED_STOP_CLAUSE, REFUSE_CLAUSE, UNREADABLE_CREDIT_FIX, UNREADABLE_CREDIT_REASON, refusesCreation, throttleConsequence, type BillingLimitCause } from '../utilities/computeStop'
import { MIN_AUTO_REFILL_CENTS, autoReloadCanFire, capAlertMessage, capAlertStillReached, capExhaustedClause, creditThrottleCause, refillBlock, refusedRefill } from '../utilities/credit'
import { useOrgRole } from '../hooks/useOrgRole'
import type { BillingConsequences, CreditPosition, CreditPurchase } from '../models/useBilling'

// `action` labels the /billing button with the REMEDY (upgrade, raise the cap, restore payment)
// rather than a destination — a throttled user should be offered the way out, not a noun.
//
// ⚠⚠ `secondary` EXISTS BECAUSE CREDIT GIVES SOME STATES TWO WAYS OUT (app#145): add credit, or
// upgrade. A single `action` label could name only one of them, and the cheaper way out would
// have gone unsaid. It carries its own destination because the credit controls live on the usage
// page, not on /billing — the primary button's fixed `/billing` link is exactly why one label was
// not enough.
//
// ⚠ The discipline the `blocked` branch already keeps is unchanged: the PRIMARY action is the one
// that resolves the state fastest, and the cheaper way out is still named. `secondary` makes that
// a button instead of a sentence where a button is clearer; it never replaces the primary.
type BannerAction = { label: string; to: string }
type Banner = {
  key: string
  severity: 'warning' | 'error'
  message: string
  dismissible: boolean
  action?: string
  secondary?: BannerAction
}

// NAMING THE LIMIT IS THE WHOLE POINT OF THESE THREE HELPERS. `billing_alerts.axis` has always
// carried which allowance tripped, and this banner threw it away: "You've hit your free plan
// limit — upgrade to add more" told a user they were over something without saying over WHAT, on
// a plan that meters four separate things. The remedy differs per axis too — deleting files fixes
// storage and does nothing for bandwidth — so an unnamed limit is not even actionable.
//
// `nodes` is a COUNT limit rather than something consumed, so it reads as a limit and the other
// three read as allowances. An axis missing from this map falls back to generic copy rather than
// rendering `undefined`: the router owns this vocabulary (src/billing/alerts.go) and can add to it
// without an app deploy, and a banner that says less is better than one that says nonsense.
const AXIS_NOUN: Record<string, string> = {
  bandwidth: 'bandwidth allowance',
  storage: 'storage allowance',
  nodes: 'node limit',
  compute: 'compute allowance',
}

// The included amount, when the plan row carries it — so the banner reads "bandwidth allowance
// (100 GB)" rather than sending the user to another screen to find the number.
//
// ⚠ Guarded on `> 0`, and not only for tidiness: 0 means UNLIMITED on these columns, which is why
// an unlimited axis fires no alert at all (the router's allowanceAlert returns "" for a
// non-positive pool). A 0 reaching here means the plan is unloaded or the axis has no pool, and
// "(0 B)" would be a fabricated limit. `compute` has no PlanRow column, so it is named without a
// number.
function axisAmount(plan: PlanRow | null, axis: string): string | undefined {
  if (!plan) return undefined
  if (axis === 'bandwidth') return plan.bandwidthBytesFree > 0 ? formatBytes(plan.bandwidthBytesFree) : undefined
  if (axis === 'storage') return plan.storageBytesFree > 0 ? formatBytes(plan.storageBytesFree) : undefined
  if (axis === 'nodes') return plan.nodeLimit > 0 ? `${plan.nodeLimit} ${plan.nodeLimit === 1 ? 'node' : 'nodes'}` : undefined
  return undefined
}

// Every axis that tripped this kind. More than one can be over at once — the router fires one row
// per (org, period, kind, axis) — and naming only the first would be the same defect in a smaller
// form. SORTED, because it also keys the dismissal and an unstable order would make one state look
// like two. Returns [] when nothing tripped OR when nothing that tripped is named above, which is
// what lets each caller fall back to its old generic sentence.
const trippedAxes = (alerts: BillingAlert[], kind: string): string[] =>
  alerts.filter(a => a.kind === kind && AXIS_NOUN[a.axis]).map(a => a.axis).sort()

const axisPhrases = (axes: string[], plan: PlanRow | null): string[] =>
  axes.map(axis => {
    const amount = axisAmount(plan, axis)
    return amount ? `${AXIS_NOUN[axis]} (${amount})` : AXIS_NOUN[axis]
  })

const joinPhrases = (parts: string[]): string =>
  parts.length <= 2 ? parts.join(' and ') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`

// Derive the single most-severe banner from the billing state.
//
// `throttled` (organizations.throttled_until in the future, 2.11) is the authoritative "your
// bandwidth IS reduced right now" signal, so it is surfaced first. It is a WARNING and it is
// dismissible — EXCEPT when its cause is a lapse, which is blocking rather than degrading and so
// keeps the same error/permanent treatment as the `lapsed` banner below: throttling is
// degradation, not blocking — every node the customer runs THEMSELVES stays online and every
// function still works, only the billed legs are capped — and a bar that cannot be cleared reads
// as an outage while its own copy
// says it isn't (app#127). The Usage modal carries the same state
// persistently, which is where a permanent record belongs: a panel the user opens, not a bar they
// cannot escape. The reason is derived from the same status/plan/alert signals, and it keys the
// banner so dismissing one cause does not silently hide a later, different one. Below that sit the
// advisory states: dunning (live subscription_status) and the edge-triggered spend-cap / allowance
// alerts (2.10). Returns null when there's nothing to surface.
function computeBanner(
  status: string,
  plan: PlanRow | null,
  isPaid: boolean,
  isFree: boolean,
  throttled: boolean,
  blocked: boolean,
  alerts: BillingAlert[],
  // The spend cap — the payload the `cap_*` branches below check the alert row against.
  // ⚠ The Owner's only since app#376: router migration 0222 withholds
  // `organizations.spend_cap_cents`, and the Owner reads the cap from `credit` op=status. NULL for
  // an Admin or a Member, until the credit position is read, and when no cap is set. With NULL, no
  // `cap_*` banner shows: the cap is money data (PERMISSIONS.md § 5 Org).
  spendCapCents: number | null,
  // ⚠ NULL for a Member (the tool is Owner-only), for a router predating it, and on an ARREARS
  // deployment. Every arm that READS a credit position is guarded on it, so each falls through to
  // the arm that names no credit state — which is what makes this safe to ship while prod bills
  // arrears.
  //
  // ⚠ That fall-through arm no longer names the spend cap either (app#288): what it falls to must
  // be true for all three readers, and only the allowance is.
  //
  // ⚠⚠ THE `cap_*` ARMS ARE THE EXCEPTION SINCE app#285, AND THEY ARE GUARDED ON `spendCapCents`
  // INSTEAD. They state a credit-model fact — the cap bounds automatic refills and pauses no usage
  // directly (nobgp-router#1328: it is one hop upstream of the empty balance that does) — without
  // asking `credit`, deliberately: a Member has no credit position and must not be told
  // the retired arrears sentence, and the payload cap is the thing those arms actually name. That
  // is safe because no deployment enforces the cap on usage any more (`CheckBillingBlock` lost its
  // `spend_cap` verdict at router#1279 and `RefreshThrottleFlags` no longer reads the column), so
  // there is no arrears deployment left for the old copy to be true on.
  credit: CreditPosition | null,
  // ⚠ The month's purchase rows, newest first, or null when they could not be read. Only the
  // refused-refill arm asks them.
  creditPurchases: CreditPurchase[] | null,
  // ⚠ NULL on a router predating router#1306 and for every reader who cannot load the summary.
  // Null keeps today's wording on both branches below; it is never read as "nothing enforced".
  consequences: BillingConsequences | null,
): Banner | null {
  const has = (kind: string) => alerts.some(a => a.kind === kind)

  // ⚠⚠ A SPEND-CAP BANNER IS PAID-ONLY, AND THE PLAN IS WHAT SAYS SO (app#158). Every `cap_*`
  // branch below used to be reachable by any org holding the alert row, with no plan condition. It
  // stayed off a Free screen only because the router does not write a `cap_*` alert for a plan that
  // bills no overage — a DATA dependency doing a plan gate's job. It holds exactly as long as the
  // router's alert-writing rules stay as they are, and nothing on this side would notice if they
  // changed. After app#157 the failure is worse than a wrong sentence: "Raise the cap" sends a Free
  // org to a screen where the cap control has been REMOVED — an instruction with nowhere to carry
  // it out. Benoit, 2026-08-27: "no spend cap on free, it does not make sense since there is no
  // overage or credit or auto-refill etc…".
  //
  // ⚠ Same predicate as everywhere else (`isFreePlan`, arriving as `isFree`) — no second way of
  // asking whether an org is Free — and the same null handling: `isFreePlan(null)` is false, so an
  // UNKNOWN plan KEEPS the cap banner. Hiding it from an org that really refills takes away its
  // warning that automatic refills have stopped; showing it to an unknown plan is cosmetic.
  //
  // ⚠ Gating cap_80 as well is not scope creep, it is what makes the cap_100 gate coherent: a
  // suppressed cap_100 falls THROUGH to the next branch, and an ungated cap_80 would catch it and
  // print "Automatic refills are at 80% of this month's cap" — the same paid-only concept, one
  // severity down. ⚠ And a Free org is not left without a remedy by this: the cap says nothing
  // true about its plan, while what actually limits it — the allowance, the throttle, the block —
  // keeps its own banner below, each with its own action.
  const hasCap = (kind: 'cap_80' | 'cap_100') => !isFree && has(kind)

  // ⚠⚠ BLOCKED OUTRANKS THROTTLED AND IS CHECKED FIRST. They are separate columns because they
  // are separate facts (router#966): throttling SHAPES bytes to 1 Mbit/s, blocking REFUSES them.
  // A blocked org is a Free org past its bandwidth allowance; paid plans still throttle. The two
  // stamps could in principle both be live, and "your bandwidth is limited" is a lie told to
  // somebody whose traffic has stopped — so the more severe fact wins outright rather than
  // merging into one sentence.
  //
  // ⚠ It is NOT dismissible, by this file's own rule: degradation is dismissible, blocking is
  // not. A block is the state the `lapsed` branch below already treats as permanent.
  //
  // ⚠⚠ THE COPY HAS TO FIGHT THE SCREEN, WHICH IS WHY IT IS THIS LONG. Everything around a
  // blocked org looks healthy — the agents keep their connections, presence is live, the
  // directory lists every node, the files are all still there and still listable. A customer who
  // reads "stopped" and then sees a working fleet concludes the message was about something
  // else and stops reading billing messages. So the banner says what still works before it says
  // what does not.
  //
  // ⚠ Two ways out and one of them is FREE: the stamp lapses at the start of the next billing
  // period on its own, or a paid plan lifts it. Offering only the upgrade reads as a paywall on
  // a limit that clears by itself in days, which is both wrong and the kind of wrong that gets
  // screenshotted. ⚠ And say "within a few minutes", never "immediately" — the stamp is a
  // renew-with-TTL, so an upgraded org resumes when the evaluator stops renewing it, not on the
  // next page load. A promise of instant is a support ticket four minutes later.
  if (blocked) {
    return {
      key: 'blocked_allowance',
      severity: 'error',
      dismissible: false,
      message:
        'Data transfer is stopped — you used up your free bandwidth allowance. Your devices are still online and your files are still there; only transfers are paused. This clears on its own at the start of your next billing period, or upgrade to resume within a few minutes.',
      action: 'Upgrade',
    }
  }

  if (throttled) {
    // Why is bandwidth throttled? Same precedence the evaluator's triggers use: lapse, then spend
    // cap, then the Free bandwidth allowance. `cause` rides in the key so that dismissing (say)
    // the allowance throttle does not also swallow a later lapse throttle — the causes carry
    // different fixes, and only the message changes when one supersedes another.
    let cause: BillingLimitCause = 'limit'
    let reason = 'your billing limit was reached'
    let fix = 'See Billing.'
    let action = 'Billing'
    let secondary: BannerAction | undefined
    if (isPaid && (status === 'unpaid' || status === 'canceled')) {
      cause = 'lapsed'
      reason = 'your subscription lapsed'
      fix = 'Restore payment to resume full speed.'
      action = 'Restore payment'
    } else if (credit?.creditBilled && isPaid) {
      // ⚠⚠ ON A CREDIT DEPLOYMENT THE SPEND CAP DOES NOT THROTTLE, SO THIS ARM COMES BEFORE THE
      // CAP ARM AND REPLACES IT. `throttleCreditSQL` fires when an allowance is exhausted AND
      // either the owner switched spending off or the balance is empty; the cap bounds credit
      // PURCHASES and nothing else. Keeping "you reached your monthly spend cap" here would name
      // an object that did not cause the throttle and send the customer to raise a limit that
      // will not lift it. app#145's own table predates router#1135's switch and says `cap`.
      cause = creditThrottleCause(credit)
      if (cause === 'spending_off') {
        // ⚠ THIS ORG HAS CREDIT AND CHOSE NOT TO SPEND IT. "Add credit" is the one instruction
        // that cannot help here, which is why the two causes are separated at all.
        reason = 'your included allowance is used up and paying from your balance is switched off'
        fix = 'Switch it back on to resume full speed, or upgrade for a larger allowance.'
        action = 'Upgrade'
        secondary = { label: 'Credit settings', to: '/usage' }
      } else if (cause === 'credit_landed') {
        // ⚠⚠ THIS ORG JUST BOUGHT CREDIT (app#269). The stamp is fail-open and lapses rather than
        // clears, so for up to six minutes after a purchase the banner still renders — and the
        // two-way form said "your credit balance is empty" and offered "Add credit" to a customer
        // looking at this screen because they had just added it. The balance is positive, so the
        // only true sentence is that the limit is lifting. No "Add credit" here. Upgrade stays: the
        // allowance IS used up, credit now pays metered overage, and a larger allowance is the
        // standing alternative the other two arms offer too. Dismissible like them.
        reason =
          'your included allowance is used up. Your balance is positive, or a purchase is settling, so full speed resumes within a few minutes'
        fix = 'Upgrade for a larger allowance.'
        action = 'Upgrade'
      } else {
        reason = 'your included allowance is used up and your credit balance is empty'
        // ⚠⚠ THREE STATES, NOT TWO, AND THE THIRD ONE ARRIVED WITH app#195. This asked
        // `autoReloadOn` and fell silent about the rule whenever it read On — correct while the
        // switch refused to arm under a $0 cap, and wrong the moment it stopped: a $0 cap is the
        // DEFAULT (router#933), so the ordinary result of pressing the switch is a rule that reads
        // On and can buy nothing. That customer is the one standing here — allowance used up,
        // balance empty — and this banner was telling them to add credit while saying nothing
        // about the cap that will drain it again. Adding credit is the remedy they can act on
        // today; raising the cap is the one that stops them coming back.
        //
        // ⚠⚠ AND THE THIRD STATE IS ITSELF THREE (app#223). "its monthly cap has nothing left" is
        // true of a cap that has been SPENT and false of one below the card floor, where nothing
        // has been taken from it at all — it is refused by the network, not used up, and a
        // customer who checks their refill history will not find the spend that sentence implies.
        // `refillBlock` is the same question `CreditBlock`'s caption ladder asks, so the two
        // surfaces cannot answer it differently. The remedy is the same for all three; only the
        // reason differs.
        // ⚠ Asked ONCE. Three calls down one ternary chain are three answers to one question, and
        // the reason the two surfaces drifted apart is that the question kept being re-asked.
        //
        // ⚠ A REFILL THAT DID NOT COMPLETE IS ASKED WHERE `CreditBlock`'s ladder asks it (app#271):
        // under a rule that CAN fire, after the cap rungs. A rule that cannot fire has its remedy
        // named by the cap arm, and a failure under it is history. The sentence names no card:
        // only one of the router's three failure causes is the card, and the purchase list under
        // Usage credit carries the row's own sentence.
        const block = refillBlock(credit)
        fix = autoReloadCanFire(credit)
          ? refusedRefill(creditPurchases)
            ? 'Add credit to resume full speed, or upgrade for a larger allowance. The last automatic refill did not complete. See the purchase list under Usage credit.'
            : 'Add credit to resume full speed, or upgrade for a larger allowance.'
          : block === 'cap_zero'
            ? 'Add credit to resume full speed, or upgrade for a larger allowance. Auto-refill is on, but its monthly cap is $0 — raise the cap to let it buy.'
            : block === 'cap_below_floor'
              ? `Add credit to resume full speed, or upgrade for a larger allowance. Auto-refill is on, but its monthly cap is below the ${formatCents(MIN_AUTO_REFILL_CENTS)} an automatic refill has to be worth — raise the cap to let it buy.`
              : block === 'cap_used'
                ? `Add credit to resume full speed, or upgrade for a larger allowance. Auto-refill is on, but its ${capExhaustedClause(credit)} — raise the cap to let it buy.`
                : 'Add credit to resume full speed, or upgrade for a larger allowance. Auto-refill buys more before the balance runs out.'
        action = 'Upgrade'
        secondary = { label: 'Add credit', to: '/usage' }
      }
      // ⚠⚠ `|| isPaid` IS THE FALLBACK AND IT IS NOT BELT-AND-BRACES. The throttle SQL compares
      // ACTUAL `computed_overage_cents >= spend_cap_cents`, while `cap_100` is an edge-triggered
      // alert on PROJECTED overage that router#1011 also CLEARS when the customer raises the cap —
      // so a paid, non-lapsed org can be throttled with no alert row, and there is no other paid
      // trigger it could be. Without this the banner fell to `limit` while `Usage.tsx` derived
      // `cap` from `isPaid` alone, and since app#163 those two causes disagree about whether
      // machines go offline: one screen said "your nodes stay online", the other said they are
      // stopped. The two surfaces now derive the same cause from the same state.
      //
      // ⚠⚠ AND THE SENTENCE IT FALLS TO NAMES NO CAP (app#288). This arm is reached only when
      // `credit` is null — a Member, an older router, an arrears deployment — and it carried the
      // arrears sentence app#285 removed from the two `cap_*` banners beside it: "you reached
      // your monthly spend cap … Raise the cap to resume full speed". A Member of a throttled
      // paid org read that the cap slowed the network, and was offered a remedy that their role
      // refuses and that lifts nothing. `UNREADABLE_CREDIT_REASON` and `UNREADABLE_CREDIT_FIX`
      // carry the replacement, and `Usage.tsx` reads the same two constants — the app#163 rule,
      // one home per sentence, because this defect is what two homes produce.
    } else if (hasCap('cap_100') || isPaid) {
      // ⚠ `Raise the cap` goes with the sentence that named it. `Upgrade` is the remedy that is
      // true on every regime this arm can be under, and it is the label the Free arm below and
      // all three credit arms above already use. The credit remedy stays in the sentence and
      // gets no `secondary` button: those arms offer one because a non-null position proves the
      // reader is an Owner on a credit deployment, and here neither is known.
      cause = 'cap'
      reason = UNREADABLE_CREDIT_REASON
      fix = UNREADABLE_CREDIT_FIX
      action = 'Upgrade'
    } else if (isFree) {
      cause = 'allowance'
      reason = 'you used up your free bandwidth allowance'
      fix = 'Upgrade to resume full speed.'
      action = 'Upgrade'
    }
    // ⚠ `lapsed` is the exception, and it is not a small one: the evaluator's FIRST trigger is
    // lapse, so a lapsed paid org is throttled within a couple of minutes and this branch — not
    // the `lapsed` banner below — is what it actually sees. Letting it be dismissed here would
    // make that banner's deliberate permanence unreachable in practice, and downgrade to a
    // warning a state where new usage is PAUSED rather than merely slowed. Degradation is
    // dismissible; blocking is not.
    const lapsed = cause === 'lapsed'
    // ⚠⚠ THE CONSEQUENCE VARIES BY CAUSE AND MUST (app#163). `lapsed` and `credit_empty` also
    // reach the COMPUTE stop (router#1242), which ends the ECS task a provisioned node's agent
    // runs inside — so for those two causes "your nodes stay online" was false. `allowance` is
    // bandwidth-only and keeps the sentence unchanged. `throttleConsequence` is the one home for
    // both forms, and `Usage.tsx`'s alert reads the SAME function: two surfaces describing one
    // behaviour is how they come to describe it two ways.
    //
    // ⚠⚠ AND WITH `consequences` SERVED, THE ROUTER'S FLAGS DECIDE WHICH CLAUSES STAND (app#270).
    // The stop clause follows `stop_compute` inside `throttleConsequence`, and the creation
    // refusal is added on `refuse_create` — the one consequence live today, which this banner
    // never named. The opening "Bandwidth limited to 1 Mbps" does not read `slow_bandwidth`:
    // this branch renders only under a `throttled_until` stamp, and the evaluator stamps it only
    // while enforcement is on. Null keeps today's sentence.
    const refused = refusesCreation(cause, consequences) ? ` ${capitalize(REFUSE_CLAUSE)}.` : ''
    return {
      key: `throttled_${cause}`,
      severity: lapsed ? 'error' : 'warning',
      dismissible: !lapsed,
      message: `Bandwidth limited to 1 Mbps per node — ${reason}.${refused} ${throttleConsequence(cause, consequences)} ${fix}`,
      action,
      secondary,
    }
  }

  // Not (yet) throttled — advisory / dunning states.
  if (isPaid && (status === 'unpaid' || status === 'canceled')) {
    // ⚠ A lapse is precisely what `StopLapsed` stops, so this banner claimed the opposite of
    // what the router does to a provisioned machine.
    //
    // ⚠⚠ WITH `consequences` SERVED, ONLY THE CLAUSES THE ROUTER'S FLAGS MAKE TRUE ARE SAID
    // (app#270). On the deployment we run today that is the creation refusal alone — the stop
    // needs enforcement on — and a customer told a running machine was stopped can restart work
    // that never died. The banner still renders with every clause false: the lapse itself and
    // the remedy are always true. Null keeps today's sentence.
    let message: string
    if (consequences) {
      const refused = refusesCreation('lapsed', consequences) ? ` — ${REFUSE_CLAUSE}` : ''
      const machines = consequences.stopCompute
        ? `Your own machines stay online, and ${PROVISIONED_STOP_CLAUSE}.`
        : 'Your machines stay online.'
      message = `Your subscription has lapsed${refused}. ${machines} Restore payment to resume.`
    } else {
      message = `Your subscription has lapsed — new usage is paused and billing has stopped. Your own machines stay online, and ${PROVISIONED_STOP_CLAUSE}. Restore payment to resume.`
    }
    return { key: 'lapsed', severity: 'error', dismissible: false, message }
  }
  if (status === 'past_due') {
    return {
      key: 'past_due',
      severity: 'error',
      dismissible: true,
      // ⚠ THIS ONE IS CORRECT AND IS DELIBERATELY NOT SWEPT WITH THE TWO ABOVE. `CheckBillingBlock`
      // returns `lapsed` only for `unpaid` / `canceled`, so `past_due` fires no compute stop —
      // and this branch is reached only when the org is NOT throttled, i.e. not over its cap
      // either. Nothing here goes offline. Do not "make the banners consistent".
      message: 'Payment failed — update your card to keep your subscription. Your nodes stay online.',
    }
  }
  // ⚠⚠ THE CAP BOUNDS AUTOMATIC REFILLS AND NOTHING ELSE, AND THESE TWO SENTENCES SAY SO (app#285).
  // Under credit (router#1195, #1264, #1279) the spend cap is the ceiling on what auto-refill may
  // buy in a period: it pauses no usage, stops no machine, slows nothing DIRECTLY —
  // `CheckBillingBlock` has had no `spend_cap` verdict since router#1279.
  //
  // ⚠⚠ AND IT IS STILL ONE HOP UPSTREAM OF ALL THREE (nobgp-router#1328). For an org with a rule
  // armed: cap exhausted, refill truncated to nothing, balance drains, throttled. That is why
  // `Raise the cap` below is a REAL remedy and not a leftover — and since router#1328 raising it
  // attempts the refill at once rather than waiting for the next 15-minute sweep.
  //
  // This branch said "new billable usage is paused
  // across the board. Raise the cap to resume", the retired arrears enforcement: a customer at
  // their cap was told their usage was paused while it flowed, and offered a remedy that resumed
  // nothing. The alert MAIL was rewritten for credit in router#1264; this banner was the sink that
  // rewrite did not reach. `capAlertMessage` carries the sentence, and its `cap_100` form is the
  // same definition the credit block's caption uses, so the two surfaces cannot drift again.
  //
  // ⚠⚠ NOTHING RENDERS WITHOUT A CAP ON THE PAYLOAD, WHATEVER ROW THE ORG HOLDS. The router's
  // ladder read an unset cap as $0 and wrote `cap_100` for an org that refilled under a cap and
  // then removed it (router#1322; measured 2026-09-06 on prod, org `af35bf34`, cap unset). The
  // router half stops writing the row; until it ships this gate is what keeps the banner from
  // trusting a stale row over `spend_cap_cents`, and after it ships the gate still holds — a row
  // written before the fix survives until the next sweep clears it. Both kinds fall through
  // together (same cap, same null), which is the property the `hasCap` note above protects: a
  // suppressed `cap_100` must not land in `cap_80`'s sentence.
  //
  // ⚠ `hasCap` — the paid-only gate (app#158), unknown plan KEEPS the banner — is unchanged, and
  // so are the key, the severity, the dismissal and the `Raise the cap` action: raising the cap IS
  // the way to refill again this month, which the sentence now says.
  // ⚠⚠ AND NOTHING RENDERS WHEN THE REFILLS NO LONGER REACH THE CAP (app#291). The row is a latch
  // cleared by a ONCE-A-DAY sweep, and the cap under it moves the moment the customer takes this
  // banner's own remedy — so a customer who pressed `Raise the cap` was told for up to a day that
  // they had reached a figure they never reached, that refills were stopped while they were
  // running again, and to raise the cap they had just raised.
  //
  // ⚠⚠ `capAlertStillReached` ANSWERS null FOR A MEMBER, AND THE LATCH IS KEPT THERE. `credit` is
  // Owner-only, so a Member cannot see the refill figure and this cannot be evaluated for them.
  // The asymmetry is deliberate and is the smaller wrong: only an Owner can raise a cap, so only
  // an Owner is shown a remedy — and the alternative, putting this organization's spend figure on
  // a Member-readable payload, is the direction router#1327 just moved AWAY from. If that trade
  // is wrong, the fix is to widen the payload, not to drop this gate.
  if (hasCap('cap_100') && capAlertStillReached('cap_100', spendCapCents, credit) !== false) {
    const message = capAlertMessage('cap_100', spendCapCents)
    if (message) return { key: 'cap_100', severity: 'error', dismissible: true, message, action: 'Raise the cap' }
  }
  if (hasCap('cap_80') && capAlertStillReached('cap_80', spendCapCents, credit) !== false) {
    const message = capAlertMessage('cap_80', spendCapCents)
    if (message) return { key: 'cap_80', severity: 'warning', dismissible: true, message }
  }
  if (has('allowance_100')) {
    // ⚠ The axes ride in the KEY, the same reason `throttled_${cause}` does: dismissing the
    // bandwidth banner must not also swallow a storage one that trips an hour later. The two name
    // different limits and take different fixes.
    const over = trippedAxes(alerts, 'allowance_100')
    const named = joinPhrases(axisPhrases(over, plan))

    // ⚠⚠ THREE STATES, NOT A TERNARY (app#141). `isFree` and `isPaid` are BOTH false when the plan
    // row is unknown, and an `isFree ? … : …` hands that case to the PAID sentence — a Free org
    // told "metered overage now applies", which is false, and denied the Upgrade button, which is
    // the one thing that would fix its problem. Free/paid/unknown are three answers and the code
    // has to have three.
    //
    // ⚠ It is NOT a load-window flicker, which is what the two-way read assumed. `plan` and
    // `alerts` are written by the same `set()`, so during loading `alerts` is empty and no banner
    // renders at all. The reachable cause is `organizations.plan_id` matching no row this reader
    // can see in `plans` — and that state PERSISTS, for as long as the bad id does.
    //
    // ⚠⚠ Returning null for the unknown case would be worse than the bug. The org IS over a
    // limit; suppressing the banner replaces a wrong sentence with permanent silence, and nothing
    // downstream would ever say so. The axis does not need the plan — only the AMOUNT does — so
    // the unknown branch still NAMES the limit and simply declines to say what it costs or to
    // promise an upgrade fixes it. The Billing button (the `action` fallback) is the way on.
    let message: string
    let action: string | undefined
    if (isFree) {
      message = named
        ? `You’ve reached your free plan’s ${named} — upgrade to add more.`
        : 'You’ve hit your free plan limit — upgrade to add more.'
      action = 'Upgrade'
    } else if (isPaid) {
      message = named
        ? `You’re over your included ${named} — metered overage now applies.`
        : 'You’re over your included allowance — metered overage now applies.'
    } else {
      message = named ? `You’ve reached your ${named}.` : 'You’ve reached an included limit.'
    }
    return {
      key: over.length ? `allowance_100_${over.join('_')}` : 'allowance_100',
      severity: 'warning',
      dismissible: true,
      message,
      action,
    }
  }
  if (has('allowance_80')) {
    const near = trippedAxes(alerts, 'allowance_80')
    return {
      key: near.length ? `allowance_80_${near.join('_')}` : 'allowance_80',
      severity: 'warning',
      dismissible: true,
      message: near.length
        ? `You’re approaching your ${joinPhrases(axisPhrases(near, plan))}.`
        : 'You’re approaching your included allowance.',
    }
  }
  return null
}

// App-wide billing banner (rendered in Layout). Most banners are dismissible per session (a new or
// worse state re-shows because the dismissed key no longer matches), and the dismissal is plain
// `useState`, so it lasts only as long as the mount — a reload brings the banner back while the
// state is still true. A lapse is the one that stays — in BOTH its forms, throttled and not:
// there, usage is actually PAUSED rather than slowed, which is the distinction the throttle banner
// was getting wrong. Links to /billing
// (role-safe: non-Owners land on the read-only billing screen rather than hitting a 403).
export default function BillingBanner() {
  const { status, plan, spendCapCents, throttledUntil, blockedUntil, alerts, credit, creditPurchases, creditLoaded, loadCredit, consequences } = useBilling()
  const setBilling = useBilling(s => s.set)
  const activeOrgId = useOrganizations(s => s.activeOrgId)
  const { can } = useOrgRole()
  const canManageBilling = can('billing.manage') // Owner only — the router re-checks

  // ⚠ The banner is app-wide, so it loads the credit position itself rather than waiting for the
  // usage page to be opened. Owner-gated for the reason Billing.tsx gates `loadSummary`: the tool
  // is Owner-only, and a Member's refused read would put an error in the console on every org
  // switch and change nothing on their screen. A Member falls to the throttle arm that names no
  // credit state, which is correct — that arm reads no position, and since app#288 it names the
  // OWNER as the one who can act, because every credit remedy is an Owner action a Member could
  // not take anyway.
  //
  // ⚠⚠ CLEARED ON EVERY ORG SWITCH, BEFORE ANYTHING IS FETCHED — the rule `Billing.tsx` already
  // applies to the card and the invoices, and for the same reason: the billing store is app-wide,
  // `get()` does not touch this field, and the loader below is guarded on `creditLoaded`, so
  // without this the position of the org the customer just LEFT would keep driving the banner. It
  // is not a stale number but a claim about another organization's settings — "paying from your
  // balance is switched off" for an org whose switch is on — and for a Member of the new org the
  // read never runs, so it would never be replaced at all. The clear runs first, `creditLoaded`
  // goes false, and the loader below refetches.
  useEffect(() => {
    // ⚠ `spendCapCents` too since app#376: it is read with the credit position now (router
    // migration 0222 withholds the column), so it belongs to the org the position belongs to.
    setBilling({ credit: null, creditLoaded: false, spendCapCents: null })
  }, [activeOrgId])

  useEffect(() => {
    if (canManageBilling && !creditLoaded) void loadCredit()
  }, [canManageBilling, creditLoaded, loadCredit])
  const [dismissed, setDismissed] = useState<string>()
  const [, setTick] = useState(0)

  // throttled_until is a future timestamp while throttled; the evaluator renews it (realtime keeps
  // it fresh), but when the org recovers the stamp simply lapses with no DB event. Schedule a
  // re-render at expiry so the banner clears on its own when the limit ends.
  // ⚠ blocked_until needs the SAME self-clearing timer, and for a sharper reason: a block ends
  // by the evaluator simply STOPPING renewal, so there is no DB event and no realtime message to
  // wake the reader. Without this the banner would sit on screen — non-dismissible — after the
  // block had already ended. Both stamps are watched, because either can be the live one.
  useEffect(() => {
    const stamps = [throttledUntil, blockedUntil].filter((v): v is string => !!v)
    const timers = stamps.map(v => {
      const ms = new Date(v).getTime() - Date.now()
      return ms > 0 ? setTimeout(() => setTick(n => n + 1), ms + 1000) : undefined
    })
    return () => timers.forEach(t => t && clearTimeout(t))
  }, [throttledUntil, blockedUntil])

  const throttled = !!throttledUntil && new Date(throttledUntil).getTime() > Date.now()
  const blocked = !!blockedUntil && new Date(blockedUntil).getTime() > Date.now()
  const banner = computeBanner(status, plan, isPaidPlan(plan), isFreePlan(plan), throttled, blocked, alerts, spendCapCents, credit, creditPurchases, consequences)
  if (!banner || (banner.dismissible && dismissed === banner.key)) return null

  return (
    <Box
      sx={{
        position: 'fixed',
        top: MENU_HEIGHT + 8,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: Z_INDEX.BANNER,
        width: 'min(640px, calc(100% - 32px))',
      }}
    >
      <Alert
        severity={banner.severity}
        variant="filled"
        action={
          <>
            {/* ⚠ The SECOND remedy renders FIRST, to the left of the primary. It is the cheaper
                way out (add credit rather than upgrade a plan), and putting it after the primary
                would offer the bigger commitment first. The primary keeps its fixed /billing
                link; only this one carries a destination, because the credit controls live on the
                usage page. */}
            {banner.secondary && (
              <Button color="inherit" size="small" component={Link} to={banner.secondary.to}>
                {banner.secondary.label}
              </Button>
            )}
            <Button color="inherit" size="small" component={Link} to="/billing">
              {banner.action ?? 'Billing'}
            </Button>
            {banner.dismissible && (
              <IconButton color="inherit" size="small" aria-label="Dismiss" onClick={() => setDismissed(banner.key)}>
                <LuX size={16} />
              </IconButton>
            )}
          </>
        }
      >
        {banner.message}
      </Alert>
    </Box>
  )
}
